import { apiFetch, apiGet, apiSend } from '../../session/client';

/**
 * "My clinics" (Just Treated for Clinics, stage A14): everything the patient's side of the website reads
 * and writes, and nothing it invents. Every route is the signed-in person's own: the platform decides
 * which clinics have linked them as a patient, and answers 404 for anything that is not theirs.
 */

export interface ClinicAppointment {
  readonly id: string;
  readonly startsAt: string;
  readonly endsAt: string;
  /** PENDING waits for the deposit: it is never shown as confirmed. */
  readonly status: 'HELD' | 'PENDING' | 'CONFIRMED' | 'ATTENDED' | 'NO_SHOW' | 'CANCELLED' | 'EXPIRED';
  readonly upcoming: boolean;
  readonly items: readonly {
    readonly tid: string;
    readonly serviceName: string;
    readonly kind: 'CONSULTATION' | 'TREATMENT' | 'PATCH_TEST' | 'COURSE';
    readonly category: string;
    readonly areas: readonly string[];
  }[];
  readonly deposit: { readonly amountMinor: number; readonly status: 'NOT_REQUIRED' | 'REQUIRED' | 'PAID' };
  readonly expiresAt?: string;
}

export interface ClinicTask {
  readonly kind: 'MEDICAL_HISTORY' | 'QUESTIONNAIRE' | 'CONSENT_PREREAD' | 'DEPOSIT';
  readonly state: 'TO_DO' | 'DONE' | 'WAITING';
  readonly appointmentId?: string;
  readonly tid?: string;
  readonly subject?: string;
  readonly doneAt?: string;
  readonly clarificationRequested?: boolean;
}

export interface ClinicCounts {
  readonly tasksToDo: number;
  readonly followUpsDue: number;
  readonly messages: number;
  readonly photos: number;
}

export interface ClinicCard {
  readonly organisationId: string;
  readonly name: string;
  readonly nextAppointment?: ClinicAppointment;
  readonly counts: ClinicCounts;
}

export interface ClinicView {
  readonly organisationId: string;
  readonly name: string;
  readonly clientName: string;
  readonly appointments: readonly ClinicAppointment[];
  readonly tasks: readonly ClinicTask[];
  readonly counts: ClinicCounts;
  readonly safetyLine: string;
}

export interface Question {
  readonly key: string;
  readonly text: string;
  readonly kind: 'YES_NO' | 'TEXT';
  readonly detailWhenYes?: boolean;
}

export interface AnswerInput {
  readonly questionKey: string;
  readonly answer: string;
  readonly detail?: string;
}

export interface ClinicForm {
  readonly questionSet: {
    readonly id: string;
    readonly title: string;
    readonly questions: readonly Question[];
  };
  readonly given?: {
    readonly version?: number;
    readonly at: string;
    readonly answers: readonly AnswerInput[];
  };
  readonly clarificationRequested?: boolean;
  readonly subject?: string;
  readonly open: boolean;
}

export interface ConsentPreRead {
  readonly subject: string;
  readonly content?: {
    readonly id: string;
    readonly number: number;
    readonly title: string;
    readonly body: string;
    readonly statements: readonly {
      readonly key: string;
      readonly text: string;
      readonly required: boolean;
    }[];
  };
  readonly read?: { readonly at: string; readonly number: number };
}

export interface ClinicMessage {
  readonly id: string;
  readonly from: 'PATIENT' | 'CLINIC';
  readonly body: string;
  readonly at: string;
}

export interface ClinicPhoto {
  readonly id: string;
  readonly area: string;
  readonly stage: string;
  readonly source: 'CLINIC' | 'PATIENT';
  readonly orientation: number;
  readonly takenAt: string;
  readonly releasedAt?: string;
}

export interface ClinicAftercare {
  readonly followUps: readonly {
    readonly id: string;
    readonly serviceName: string;
    readonly label: string;
    readonly dueOn: string;
    readonly status: 'DUE' | 'COMPLETED';
    readonly aftercare?: { readonly title: string; readonly body: string };
    readonly checkIns: readonly { readonly id: string; readonly at: string; readonly reviewed: boolean }[];
  }[];
  readonly concerns: readonly {
    readonly id: string;
    readonly serviceName: string;
    readonly text: string;
    readonly at: string;
    readonly replies: readonly { readonly id: string; readonly body: string; readonly at: string }[];
  }[];
  readonly treatments: readonly { readonly tid: string; readonly serviceName: string }[];
  readonly safetyLine: string;
}

export interface DepositPayment {
  readonly reference: string;
  readonly clientToken: string;
  readonly testMode: boolean;
}

const e = encodeURIComponent;
const clinic = (org: string) => `/consumer/aesthetics/clinics/${e(org)}`;

export const getClinics = (): Promise<{ readonly clinics: readonly ClinicCard[] }> =>
  apiGet('/consumer/aesthetics/clinics');
export const getClinic = (org: string): Promise<ClinicView> => apiGet(clinic(org));
export const getAppointment = (org: string, id: string): Promise<ClinicAppointment> =>
  apiGet(`${clinic(org)}/appointments/${e(id)}`);
export const payDeposit = (
  org: string,
  id: string,
): Promise<{ readonly appointment: ClinicAppointment; readonly payment: DepositPayment }> =>
  apiSend('POST', `${clinic(org)}/appointments/${e(id)}/deposit`, {});
export const getHistory = (org: string): Promise<ClinicForm> => apiGet(`${clinic(org)}/medical-history`);
export const giveHistory = (org: string, answers: readonly AnswerInput[]): Promise<unknown> =>
  apiSend('POST', `${clinic(org)}/medical-history`, { answers });
export const getQuestionnaire = (org: string, tid: string): Promise<ClinicForm> =>
  apiGet(`${clinic(org)}/questionnaires/${e(tid)}`);
export const giveQuestionnaire = (
  org: string,
  tid: string,
  answers: readonly AnswerInput[],
): Promise<unknown> => apiSend('POST', `${clinic(org)}/questionnaires/${e(tid)}`, { answers });
export const getConsent = (org: string, tid: string): Promise<ConsentPreRead> =>
  apiGet(`${clinic(org)}/consent/${e(tid)}`);
export const readConsent = (org: string, tid: string, contentVersionId: string): Promise<unknown> =>
  apiSend('POST', `${clinic(org)}/consent/${e(tid)}`, { contentVersionId });
export const getMessages = (
  org: string,
): Promise<{ readonly messages: readonly ClinicMessage[]; readonly safetyLine: string }> =>
  apiGet(`${clinic(org)}/messages`);
export const sendMessage = (org: string, text: string): Promise<ClinicMessage> =>
  apiSend('POST', `${clinic(org)}/messages`, { text });
export const getPhotos = (org: string): Promise<{ readonly photos: readonly ClinicPhoto[] }> =>
  apiGet(`${clinic(org)}/photos`);
export const getAftercare = (org: string): Promise<ClinicAftercare> => apiGet(`${clinic(org)}/aftercare`);
export const raiseConcern = (org: string, tid: string, text: string): Promise<unknown> =>
  apiSend('POST', `${clinic(org)}/aftercare/concerns`, { tid, text });

/** A photograph's bytes, through the API (there is no URL to a photograph). */
export async function photoBlob(org: string, id: string): Promise<Blob> {
  const response = await apiFetch(`${clinic(org)}/photos/${e(id)}/content`, { method: 'GET' });
  if (!response.ok) throw new Error('We could not load this photograph.');
  return response.blob();
}
